import { AsyncProcess, SurveyInitiative } from '@g17eco/types/survey';
import { DataPeriods } from '@utils/dataPeriods';
import { SurveyScope } from '@g17eco/types/surveyCommon';
import { g17ecoApi, transformResponse } from './g17ecoApi';
import { ApiResponse } from '@g17eco/types/api';
import { AggregationMode } from '@g17eco/core';
import { JobStatus } from '@g17eco/types/background-jobs';
import { SURVEY_DATA_TAG } from './surveys';

const TAGS = ['aggregated-surveys'];

interface UpdateAllParams {
  initiativeId: string;
}

interface AggregatedSurveyConfig {
  name: string;
  surveyIds: string[];
  period?: DataPeriods;
  scope?: SurveyScope;
  aggregationMode?: AggregationMode;
}

interface CreateAggregatedSurveyParams extends AggregatedSurveyConfig {
  initiativeId: string;
}

interface AggregatedSurveyResponse {
  createdSurvey: SurveyInitiative;
  aiSummaryJob?: {
    jobId: string;
    status: JobStatus;
  };
}

const getUrl = ({ initiativeId }: { initiativeId: string; dashboardId?: string }) =>
  `/initiatives/${initiativeId}/aggregated-survey`;

export const aggregatedSurveysApi = g17ecoApi
  .enhanceEndpoints({
    addTagTypes: [...TAGS, SURVEY_DATA_TAG],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      updateAllAggregatedSurveys: builder.mutation<any, UpdateAllParams>({
        invalidatesTags: [...TAGS, SURVEY_DATA_TAG],
        transformResponse,
        query: ({ initiativeId }) => ({
          url: `${getUrl({ initiativeId })}/update-all`,
          method: 'post',
          data: { initiativeId },
        }),
      }),
      updateAggregatedSurvey: builder.mutation<ApiResponse, CreateAggregatedSurveyParams & { surveyId: string }>({
        invalidatesTags: [...TAGS, SURVEY_DATA_TAG],
        transformResponse,
        query: ({ initiativeId, surveyId, ...data }) => ({
          url: `${getUrl({ initiativeId })}/${surveyId}/update-aggregate-survey-config`,
          method: 'patch',
          data,
        }),
      }),
      createAggregatedSurvey: builder.mutation<AggregatedSurveyResponse, CreateAggregatedSurveyParams>({
        transformResponse,
        query: ({ initiativeId, ...data }) => ({
          url: `${getUrl({ initiativeId })}/aggregate`,
          method: 'post',
          data,
        }),
      }),
      getAggregatedAsyncProcesses: builder.query<AsyncProcess[] | undefined, { initiativeId: string; surveyId: string }>({
        transformResponse,
        query: ({ initiativeId, surveyId }) => ({
          url: `${getUrl({ initiativeId })}/${surveyId}/async-processes`,
          method: 'get',
        }),
      }),
    }),
  });

export const {
  useUpdateAllAggregatedSurveysMutation,
  useCreateAggregatedSurveyMutation,
  useUpdateAggregatedSurveyMutation,
  useGetAggregatedAsyncProcessesQuery,
} = aggregatedSurveysApi;
